import { Box, Collapse, CollapseProps, Flex, FlexProps, Text, TextProps, useDisclosure } from '@chakra-ui/react';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Span } from 'shared-components';

const ROW_NO_BORDER_STYLES = { borderTopWidth: 0 };

interface IContentProps extends CollapseProps {
  children: React.ReactNode;
  isOpen?: boolean;
}
interface ITriggerProps extends TextProps {
  children: React.ReactNode;
  isOpen?: boolean;
  onToggle?: () => void;
}

type CustomCollapseProps =
  | { children: React.ReactNode; defaultIsOpen?: boolean; isOpen?: never; onToggle?: never }
  | { children: React.ReactNode; defaultIsOpen?: boolean; isOpen: boolean; onToggle: () => void };
export const CustomCollapse = ({
  children,
  defaultIsOpen,
  isOpen: isOpenProps,
  onToggle: onToggleProps,
}: CustomCollapseProps) => {
  const { isOpen: isOpenDisclosure, onToggle: onToggleDisClosure } = useDisclosure({ defaultIsOpen });
  //the component state is being controlled by the parent methods when isOpen and onToggle passed from outside otherwise local disclosure is used
  const { isOpen, onToggle } = isOpenProps
    ? { isOpen: isOpenProps, onToggle: onToggleProps }
    : { isOpen: isOpenDisclosure, onToggle: onToggleDisClosure };
  return React.Children.map<React.ReactNode, React.ReactNode>(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<{ isOpen: boolean; onToggle: () => void }>, {
        isOpen,
        onToggle,
      });
    }
    return child;
  });
};
CustomCollapse.Trigger = ({ children, onToggle, isOpen, ...props }: ITriggerProps) => {
  return (
    <Span as="button" cursor="pointer" textAlign="left" display="block" mt={6} mb={6} onClick={onToggle} {...props}>
      <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronUp} />
      <Text as="span" ml="4" mb="0" color="pink.500">
        {children}
      </Text>
    </Span>
  );
};

CustomCollapse.Row = ({ children, ...props }: { children: React.ReactNode } & FlexProps) => {
  return (
    <Flex py="2" _first={ROW_NO_BORDER_STYLES} borderTopWidth={1} {...props}>
      {children}
    </Flex>
  );
};

CustomCollapse.ColLeft = ({ children, ...props }: { children: React.ReactNode } & TextProps) => {
  return (
    <Text my={0} py={0} width="40%" color="muted" {...props}>
      {children}
    </Text>
  );
};
CustomCollapse.ColRight = ({ children, ...props }: { children: React.ReactNode } & TextProps) => {
  return (
    <Text my={0} py={0} width="60%" {...props}>
      {children}
    </Text>
  );
};
CustomCollapse.Content = ({ children, isOpen, ...props }: IContentProps) => {
  return (
    <Collapse in={isOpen} {...props}>
      <Box pl="8" pb="6">
        {children}
      </Box>
    </Collapse>
  );
};
